import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatRelativeDate, propertyHref } from '../utils';
import { useCurrency } from '../../../features/currency/CurrencyContext';

const RecentlyViewed = ({ properties = [], isLoading }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const scrollRef = useRef(null);
  const list = Array.isArray(properties) ? properties : [];

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -280 : 280,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Recently viewed</h2>
          <p className="text-xs text-slate-500 mt-0.5">Pick up where you left off</p>
        </div>
        {list.length > 2 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => scroll('left')}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              type="button"
              onClick={() => scroll('right')}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-56 h-48 rounded-xl bg-slate-100 animate-pulse flex-shrink-0" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-10">
          <Icon name="Eye" size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600 mb-3">No recently viewed properties</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/property-search-browse')}
            iconName="Search"
            iconPosition="left"
          >
            Browse properties
          </Button>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'thin' }}
        >
          {list.map((property) => (
            <Link
              key={property.id}
              to={propertyHref(property)}
              className="flex-shrink-0 w-56 rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-slate-300 transition-all bg-white"
            >
              <div className="relative h-32 bg-slate-100">
                {property.image ? (
                  <img src={property.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={22} className="text-slate-400" />
                  </div>
                )}
                <span className="absolute bottom-2 left-2 bg-slate-900/85 text-white text-[11px] font-semibold px-2 py-0.5 rounded">
                  {formatPrice(property.price, 'UGX')}
                </span>
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold text-slate-900 truncate">{property.title}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{property.location}</p>
                {property.viewedDate && (
                  <p className="text-[11px] text-slate-400 mt-2">
                    Viewed {formatRelativeDate(property.viewedDate)}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default RecentlyViewed;
